import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Image,
  Video,
  FileText,
  MessageSquare,
  Clock,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Edit3,
  Send,
  Archive,
  Eye,
  ChevronDown,
  History,
  Upload,
  Package,
  Trash2,
} from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { DateManager } from '../../components/dates/DateManager';
import { WORK_ITEM_STATUSES, STATUS_FLOW } from '@yoda/shared';
import type { WorkItemDate } from '@yoda/shared';
import { api } from '../../lib/api';
import toast from 'react-hot-toast';

interface WorkItem {
  id: string;
  projectId: string;
  projectName?: string;
  bundleId?: string | null;
  requestId?: string | null;
  title: string;
  description: string | null;
  type: string;
  status: string;
  fileUrl: string | null;
  thumbnailUrl: string | null;
  fileSize: number | null;
  mimeType: string | null;
  currentVersionId: string | null;
  commentCount?: number;
  dates?: WorkItemDate[];
  createdAt: string;
  updatedAt: string;
}

interface WorkItemVersion {
  id: string;
  versionNumber: number;
  fileUrl: string;
  fileKey: string;
  thumbnailUrl: string | null;
  fileSize: number | null;
  mimeType: string | null;
  notes: string | null;
  uploadedBy: string;
  uploaderName: string;
  uploaderAvatar: string | null;
  uploadedAt: string;
}

interface Comment {
  id: string;
  content: string | null;
  voiceUrl: string | null;
  voiceDuration: number | null;
  userName: string;
  userAvatar: string | null;
  createdAt: string;
}

const statusIcons: Record<string, typeof Clock> = {
  draft: FileText,
  seeking_commentary: MessageSquare,
  approval_requested: Clock,
  changes_requested: AlertCircle,
  approved: CheckCircle2,
  archived: Archive,
};

export function WorkItemDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [workItem, setWorkItem] = useState<WorkItem | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [versions, setVersions] = useState<WorkItemVersion[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadNotes, setUploadNotes] = useState('');
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    async function fetchData() {
      try {
        setIsLoading(true);
        const [workItemRes, commentsRes, versionsRes] = await Promise.all([
          api.get<WorkItem>(`/work-items/${id}`),
          api.get<Comment[]>(`/work-items/${id}/comments`),
          api.get<WorkItemVersion[]>(`/work-items/${id}/versions`),
        ]);
        setWorkItem(workItemRes.data);
        setComments(Array.isArray(commentsRes.data) ? commentsRes.data : []);
        setVersions(Array.isArray(versionsRes.data) ? versionsRes.data : []);
        setEditTitle(workItemRes.data.title);
        setEditDescription(workItemRes.data.description || '');
        setError(null);
      } catch (err) {
        setError('Failed to load work item');
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    }
    if (id) fetchData();
  }, [id]);

  const handleUpdateDetails = async () => {
    if (!workItem || !editTitle.trim()) return;

    setIsUpdating(true);
    try {
      await api.patch(`/work-items/${workItem.id}`, {
        title: editTitle.trim(),
        description: editDescription.trim() || null,
      });

      setWorkItem({
        ...workItem,
        title: editTitle.trim(),
        description: editDescription.trim() || null,
      });
      setEditModalOpen(false);
      toast.success('Work item updated');
    } catch (err: any) {
      toast.error(err.message || 'Failed to update');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!workItem) return;

    setIsUpdating(true);
    try {
      await api.patch(`/work-items/${workItem.id}/status`, {
        status: newStatus,
      });

      setWorkItem({ ...workItem, status: newStatus });
      setStatusModalOpen(false);
      toast.success(`Status changed to ${WORK_ITEM_STATUSES[newStatus as keyof typeof WORK_ITEM_STATUSES]?.label}`);
    } catch (err: any) {
      toast.error(err.message || 'Failed to update status');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Store the file and open modal for notes
    setPendingFile(file);
    setUploadNotes('');
    setUploadModalOpen(true);

    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUploadWithNotes = async () => {
    if (!pendingFile || !id) return;

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', pendingFile);
      if (uploadNotes.trim()) {
        formData.append('notes', uploadNotes.trim());
      }

      await api.upload(`/uploads/work-item-version/${id}`, formData);
      toast.success('New version uploaded successfully!');

      // Refresh data
      const [workItemRes, versionsRes] = await Promise.all([
        api.get<WorkItem>(`/work-items/${id}`),
        api.get<WorkItemVersion[]>(`/work-items/${id}/versions`),
      ]);
      setWorkItem(workItemRes.data);
      setVersions(Array.isArray(versionsRes.data) ? versionsRes.data : []);

      // Close modal and reset
      setUploadModalOpen(false);
      setPendingFile(null);
      setUploadNotes('');
    } catch (err: any) {
      toast.error(err.message || 'Failed to upload file');
    } finally {
      setIsUploading(false);
    }
  };

  const cancelUpload = () => {
    setUploadModalOpen(false);
    setPendingFile(null);
    setUploadNotes('');
  };

  const handleDelete = async () => {
    if (!workItem) return;

    setIsDeleting(true);
    try {
      await api.delete(`/work-items/${workItem.id}`);
      toast.success('Work item deleted');
      navigate(`/projects/${workItem.projectId}`);
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete work item');
      setIsDeleting(false);
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    const variants: Record<string, 'default' | 'success' | 'warning' | 'danger' | 'info' | 'accent'> = {
      draft: 'default',
      seeking_commentary: 'warning',
      approval_requested: 'accent',
      changes_requested: 'danger',
      approved: 'success',
      archived: 'default',
    };
    return variants[status] || 'default';
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-NZ', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  if (error || !workItem) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500 mb-4">{error || 'Work item not found'}</p>
        <Link to="/projects">
          <Button variant="secondary">Back to Projects</Button>
        </Link>
      </div>
    );
  }

  const StatusIcon = statusIcons[workItem.status] || FileText;
  const statusConfig = WORK_ITEM_STATUSES[workItem.status as keyof typeof WORK_ITEM_STATUSES];
  const allowedStatuses = STATUS_FLOW[workItem.status as keyof typeof STATUS_FLOW] || [];

  return (
    <div className="space-y-6">
      {/* Hidden file input for uploads */}
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept="image/*,video/*,.pdf,.doc,.docx"
        onChange={handleFileSelect}
      />

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <Link
          to={`/projects/${workItem.projectId}`}
          className="inline-flex items-center gap-2 text-text-secondary hover:text-text mb-4 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Project
        </Link>

        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl font-bold">{workItem.title}</h1>
              <Button size="sm" variant="ghost" onClick={() => setEditModalOpen(true)}>
                <Edit3 className="w-4 h-4" />
              </Button>
            </div>
            {workItem.description && (
              <p className="text-text-secondary">{workItem.description}</p>
            )}
          </div>

          <div className="flex items-center gap-3">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              isLoading={isUploading}
            >
              <Upload className="w-4 h-4" />
              {workItem.fileUrl ? 'Upload New Version' : 'Upload File'}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setDeleteModalOpen(true)}
              className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
            <button
              onClick={() => setStatusModalOpen(true)}
              className="flex items-center gap-2"
            >
              <Badge variant={getStatusBadgeVariant(workItem.status)} size="md">
                <StatusIcon className="w-4 h-4 mr-1" />
                {statusConfig?.label || workItem.status}
              </Badge>
            </button>
          </div>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Preview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="lg:col-span-2"
        >
          <Card hover={false}>
            <CardContent className="p-0">
              <div className="relative aspect-video bg-bg-tertiary rounded-xl overflow-hidden">
                {workItem.fileUrl ? (
                  workItem.type === 'video' || workItem.mimeType?.startsWith('video/') ? (
                    <video
                      src={workItem.fileUrl}
                      controls
                      className="w-full h-full object-contain"
                    />
                  ) : workItem.type === 'image' || workItem.mimeType?.startsWith('image/') ? (
                    <img
                      src={workItem.fileUrl}
                      alt={workItem.title}
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <FileText className="w-16 h-16 text-text-muted" />
                    </div>
                  )
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <FileText className="w-16 h-16 text-text-muted" />
                  </div>
                )}

                {/* Type indicator */}
                <div className="absolute top-3 left-3">
                  <div className="p-2 rounded-lg bg-black/50 backdrop-blur-sm">
                    {(workItem.type === 'image' || workItem.mimeType?.startsWith('image/')) && <Image className="w-5 h-5 text-white" />}
                    {(workItem.type === 'video' || workItem.mimeType?.startsWith('video/')) && <Video className="w-5 h-5 text-white" />}
                    {workItem.type === 'document' && <FileText className="w-5 h-5 text-white" />}
                  </div>
                </div>

                {/* Open in new tab */}
                {workItem.fileUrl && (
                  <a
                    href={workItem.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="absolute top-3 right-3 p-2 rounded-lg bg-black/50 backdrop-blur-sm hover:bg-black/70 transition-colors"
                  >
                    <Eye className="w-5 h-5 text-white" />
                  </a>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Version Updates - prominently displayed */}
          {versions.length > 0 && (
            <Card hover={false} className="mt-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <History className="w-5 h-5" />
                  Updates
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {versions.slice(0, 5).map((version, index) => {
                  const isCurrent = workItem?.currentVersionId === version.id || (index === 0 && !workItem?.currentVersionId);
                  return (
                    <div
                      key={version.id}
                      className={`p-4 rounded-xl border ${isCurrent ? 'border-accent/30 bg-accent/5' : 'border-border bg-bg-tertiary/50'}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold">Version {version.versionNumber}</span>
                            {isCurrent && (
                              <Badge variant="accent" size="sm">Current</Badge>
                            )}
                          </div>
                          {version.notes ? (
                            <p className="text-text-secondary">{version.notes}</p>
                          ) : (
                            <p className="text-text-muted italic">No update notes</p>
                          )}
                          <div className="flex items-center gap-2 mt-2 text-sm text-text-muted">
                            <span>{version.uploaderName}</span>
                            <span>•</span>
                            <span>{formatDate(version.uploadedAt)}</span>
                          </div>
                        </div>
                        <a
                          href={version.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 rounded-lg hover:bg-bg-tertiary transition-colors text-text-muted hover:text-text"
                        >
                          <Eye className="w-4 h-4" />
                        </a>
                      </div>
                    </div>
                  );
                })}
                {versions.length > 5 && (
                  <button
                    onClick={() => setShowVersions(true)}
                    className="text-sm text-accent hover:underline"
                  >
                    View all {versions.length} versions
                  </button>
                )}
              </CardContent>
            </Card>
          )}
        </motion.div>

        {/* Details & Actions */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="space-y-4"
        >
          {/* Status Dropdown */}
          <Card hover={false}>
            <CardHeader>
              <CardTitle>Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="relative">
                <button
                  onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
                  disabled={isUpdating}
                  className="w-full flex items-center justify-between p-3 rounded-xl border border-border bg-bg-tertiary hover:border-accent/50 transition-colors disabled:opacity-50"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="p-2 rounded-lg"
                      style={{ backgroundColor: statusConfig?.bgColor }}
                    >
                      <StatusIcon className="w-4 h-4" style={{ color: statusConfig?.color }} />
                    </div>
                    <div className="text-left">
                      <p className="font-medium">{statusConfig?.label}</p>
                      <p className="text-xs text-text-muted">{statusConfig?.description}</p>
                    </div>
                  </div>
                  <ChevronDown className={`w-5 h-5 text-text-muted transition-transform ${statusDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {statusDropdownOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setStatusDropdownOpen(false)}
                    />
                    <div className="absolute top-full left-0 right-0 mt-2 py-2 rounded-xl border border-border bg-bg-secondary shadow-xl z-20 max-h-80 overflow-y-auto">
                      {Object.entries(WORK_ITEM_STATUSES).map(([status, config]) => {
                        const Icon = statusIcons[status] || FileText;
                        const isCurrent = status === workItem.status;
                        const isAllowed = allowedStatuses.includes(status as any);

                        return (
                          <button
                            key={status}
                            onClick={() => {
                              if (!isCurrent && isAllowed) {
                                handleStatusChange(status);
                                setStatusDropdownOpen(false);
                              }
                            }}
                            disabled={!isAllowed || isCurrent}
                            className={`w-full flex items-center gap-3 px-4 py-3 transition-colors ${
                              isCurrent
                                ? 'bg-accent/10'
                                : isAllowed
                                ? 'hover:bg-bg-tertiary cursor-pointer'
                                : 'opacity-40 cursor-not-allowed'
                            }`}
                          >
                            <div
                              className="p-2 rounded-lg"
                              style={{ backgroundColor: config.bgColor }}
                            >
                              <Icon className="w-4 h-4" style={{ color: config.color }} />
                            </div>
                            <div className="text-left flex-1">
                              <p className="font-medium text-sm">{config.label}</p>
                              <p className="text-xs text-text-muted">{config.description}</p>
                            </div>
                            {isCurrent && (
                              <span className="text-xs text-accent font-medium">Current</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Info */}
          <Card hover={false}>
            <CardHeader>
              <CardTitle>Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-text-muted">Type</span>
                <span className="capitalize">{workItem.type}</span>
              </div>
              {workItem.fileSize && (
                <div className="flex justify-between">
                  <span className="text-text-muted">Size</span>
                  <span>{(workItem.fileSize / 1024 / 1024).toFixed(2)} MB</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-text-muted">Created</span>
                <span>{formatDate(workItem.createdAt)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-text-muted">Updated</span>
                <span>{formatDate(workItem.updatedAt)}</span>
              </div>
            </CardContent>
          </Card>

          {/* Dates */}
          <DateManager
            workItemId={workItem.id}
            dates={workItem.dates || []}
            onDatesChange={(dates) => setWorkItem({ ...workItem, dates })}
          />

          {/* Comments */}
          <Card hover={false}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                Comments ({comments.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {comments.length === 0 ? (
                <p className="text-sm text-text-muted text-center py-4">No comments yet</p>
              ) : (
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {comments.map((comment) => (
                    <div key={comment.id} className="p-3 rounded-lg bg-bg-tertiary">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-sm">{comment.userName}</span>
                        <span className="text-xs text-text-muted">
                          {formatDate(comment.createdAt)}
                        </span>
                      </div>
                      {comment.content && (
                        <p className="text-sm text-text-secondary">{comment.content}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Version History */}
          <Card hover={false}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <History className="w-4 h-4" />
                  Version History ({versions.length})
                </CardTitle>
                <button
                  onClick={() => setShowVersions(!showVersions)}
                  className="text-xs text-accent hover:underline"
                >
                  {showVersions ? 'Hide' : 'Show'}
                </button>
              </div>
            </CardHeader>
            {showVersions && (
              <CardContent>
                {versions.length === 0 ? (
                  <p className="text-sm text-text-muted text-center py-4">
                    No versions uploaded yet
                  </p>
                ) : (
                  <div className="space-y-3 max-h-80 overflow-y-auto">
                    {versions.map((version, index) => {
                      const isCurrent = workItem?.currentVersionId === version.id || (index === 0 && !workItem?.currentVersionId);
                      return (
                        <div
                          key={version.id}
                          className={`p-3 rounded-lg ${isCurrent ? 'bg-accent/10 border border-accent/30' : 'bg-bg-tertiary'}`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium">
                                Version {version.versionNumber}
                              </span>
                              {isCurrent && (
                                <Badge variant="accent" size="sm">Current</Badge>
                              )}
                            </div>
                            <a
                              href={version.fileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-1 rounded hover:bg-bg-tertiary transition-colors"
                            >
                              <Eye className="w-4 h-4 text-text-muted" />
                            </a>
                          </div>
                          {version.notes && (
                            <p className="text-sm text-text-secondary mb-2">{version.notes}</p>
                          )}
                          <div className="flex items-center gap-2 text-xs text-text-muted">
                            <span>{version.uploaderName}</span>
                            <span>-</span>
                            <span>{formatDate(version.uploadedAt)}</span>
                            {version.fileSize && (
                              <>
                                <span>-</span>
                                <span>{(version.fileSize / 1024 / 1024).toFixed(2)} MB</span>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            )}
          </Card>

          {/* Bundle/Request Link */}
          {workItem.bundleId && (
            <Card hover={false}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-violet-500/10">
                    <Package className="w-4 h-4 text-violet-500" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-text-muted">Part of Bundle</p>
                    <Link
                      to={`/bundles/${workItem.bundleId}`}
                      className="text-sm font-medium text-accent hover:underline"
                    >
                      View Bundle
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </motion.div>
      </div>

      {/* Edit Modal */}
      <Modal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        title="Edit Work Item"
      >
        <div className="space-y-4">
          <Input
            label="Title"
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            required
          />
          <div>
            <label className="block text-sm font-medium mb-2">Description</label>
            <textarea
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              rows={4}
              className="w-full px-4 py-3 rounded-xl bg-bg-tertiary border border-border focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20 resize-none"
              placeholder="Add a description..."
            />
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setEditModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleUpdateDetails}
              disabled={!editTitle.trim() || isUpdating}
              isLoading={isUpdating}
            >
              Save Changes
            </Button>
          </div>
        </div>
      </Modal>

      {/* Status Change Modal */}
      <Modal
        isOpen={statusModalOpen}
        onClose={() => setStatusModalOpen(false)}
        title="Change Status"
        description="Select the new status for this work item"
      >
        <div className="space-y-2">
          {Object.entries(WORK_ITEM_STATUSES).map(([status, config]) => {
            const Icon = statusIcons[status] || FileText;
            const isAllowed = allowedStatuses.includes(status as any) || status === workItem.status;
            const isCurrent = status === workItem.status;

            return (
              <button
                key={status}
                onClick={() => !isCurrent && isAllowed && handleStatusChange(status)}
                disabled={!isAllowed || isCurrent || isUpdating}
                className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-colors ${
                  isCurrent
                    ? 'border-accent bg-accent/10'
                    : isAllowed
                    ? 'border-border hover:border-accent/50 hover:bg-bg-tertiary cursor-pointer'
                    : 'border-border opacity-40 cursor-not-allowed'
                }`}
              >
                <Icon className="w-5 h-5" style={{ color: config.color }} />
                <div className="text-left">
                  <p className="font-medium">{config.label}</p>
                  <p className="text-xs text-text-muted">{config.description}</p>
                </div>
                {isCurrent && (
                  <span className="ml-auto text-xs text-accent">Current</span>
                )}
              </button>
            );
          })}
        </div>
      </Modal>

      {/* Upload Version Modal */}
      <Modal
        isOpen={uploadModalOpen}
        onClose={cancelUpload}
        title="Upload New Version"
        description={pendingFile ? `Uploading: ${pendingFile.name}` : undefined}
      >
        <div className="space-y-4">
          {pendingFile && (
            <div className="p-3 rounded-lg bg-bg-tertiary flex items-center gap-3">
              <div className="p-2 rounded-lg bg-accent/10">
                <Upload className="w-5 h-5 text-accent" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{pendingFile.name}</p>
                <p className="text-sm text-text-muted">
                  {(pendingFile.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium mb-2">
              Update Notes <span className="text-text-muted font-normal">(optional)</span>
            </label>
            <textarea
              value={uploadNotes}
              onChange={(e) => setUploadNotes(e.target.value)}
              rows={4}
              className="w-full px-4 py-3 rounded-xl bg-bg-tertiary border border-border focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20 resize-none"
              placeholder="Describe what changed in this version..."
              autoFocus
            />
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={cancelUpload} disabled={isUploading}>
              Cancel
            </Button>
            <Button
              onClick={handleUploadWithNotes}
              isLoading={isUploading}
              disabled={isUploading}
            >
              <Upload className="w-4 h-4" />
              Upload Version
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete Work Item"
        description="Are you sure you want to delete this work item? This action cannot be undone."
      >
        <div className="space-y-4">
          <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20">
            <p className="text-sm text-red-400">
              This will permanently delete <strong>"{workItem?.title}"</strong> and all its versions, comments, and files.
            </p>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setDeleteModalOpen(false)} disabled={isDeleting}>
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              isLoading={isDeleting}
              disabled={isDeleting}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              <Trash2 className="w-4 h-4" />
              Delete Work Item
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
